// ============================================================
//  src/pages/auth/VerifyEmailPage.js
//  OTP-based email verification — user enters 6-digit code
//  sent to their email, then is logged straight into dashboard.
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { verifyEmail, resendVerification } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { getError } from '../../utils/helpers';
import './auth.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

export default function VerifyEmailPage() {
  const { state }          = useLocation();
  const navigate           = useNavigate();
  const { loginWithToken } = useAuth();

  const email = state?.email || '';

  const [digits, setDigits]       = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);

  const [cooldown, setCooldown]   = useState(0);
  const [resending, setResending] = useState(false);
  const timerRef                  = useRef(null);
  const inputRefs                 = useRef([]);

  // Focus first box on mount
  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  // Guard — no email means direct URL navigation
  useEffect(() => {
    if (!email) {
      toast.error('Session expired. Please register again.');
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  // ── Handlers ────────────────────────────────────────────────

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    setError('');
    if (cleaned && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits]; next[index] = ''; setDigits(next);
      } else if (index > 0) {
        const next = [...digits]; next[index - 1] = ''; setDigits(next);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft'  && index > 0)            inputRefs.current[index - 1]?.focus();
    else if   (e.key === 'ArrowRight' && index < OTP_LENGTH-1) inputRefs.current[index + 1]?.focus();
    else if   (e.key === 'Enter')                               handleSubmit();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    [...pasted].forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    setError('');
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const otp        = digits.join('');
  const isComplete = otp.length === OTP_LENGTH;

  const handleSubmit = useCallback(async () => {
    if (!isComplete) { setError('Please enter all 6 digits.'); return; }
    if (loading) return;
    setLoading(true); setError('');
    try {
      const { data } = await verifyEmail(email, otp);
      if (data.success) {
        setSuccess(true);
        if (data.data?.token) loginWithToken(data.data.token, data.data.user);
        toast.success(data.data?.already_verified
          ? 'Already verified — logging you in…'
          : 'Email verified! Welcome aboard 🎉');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
      } else {
        setError(data.message || 'Incorrect code. Please try again.');
        setDigits(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      setError(code === 'expired'
        ? 'This OTP has expired. Request a new one below.'
        : getError(err) || 'Verification failed. Please try again.');
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  }, [isComplete, loading, email, otp, loginWithToken, navigate]);

  const handleResend = async () => {
    if (resending || cooldown > 0) return;
    setResending(true); setError('');
    try {
      const { data } = await resendVerification(email);
      toast.success(data.message || 'A new OTP has been sent to your email.');
      setCooldown(RESEND_COOLDOWN);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      if (err?.response?.data?.code === 'already_verified') {
        toast('Already verified — please log in.', { icon: 'ℹ️' });
        navigate('/login', { replace: true });
      } else { toast.error(getError(err)); }
    } finally { setResending(false); }
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <div className="auth-page">

      {/* ── LEFT PANEL (hidden on mobile via auth.css) ── */}
      <div className="auth-left">
        <div className="auth-dot-grid" />
        <div className="auth-ring auth-r1" />
        <div className="auth-ring auth-r2" />
        <div className="auth-left-content">
          <span className="auth-brand">
            <div className="auth-brand-mark">T</div>
            <span className="auth-brand-name">TaskPro</span>
          </span>
        </div>
        <div className="auth-left-main">
          <div className="auth-eyebrow">
            <div className="auth-eyebrow-dot" /><span>Email Verification</span>
          </div>
          <div className="auth-headline">One Code<br /><em>Away!</em></div>
          <div className="auth-sub">
            Enter the 6-digit code we just sent to your email to activate your account instantly.
          </div>
        </div>
        <div className="auth-left-footer">
          <div className="auth-ssl"><i className="fas fa-lock" /> 256-bit SSL encrypted</div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="auth-right">
        <div className="auth-shell">

          {/* ── SUCCESS STATE ── */}
          {success ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'rgba(0,196,140,.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px', fontSize: 36, color: 'var(--success)',
              }}>
                <i className="fas fa-circle-check" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 10 }}>
                Verified!
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text-mid)' }}>Taking you to your dashboard…</p>
              <span className="spinner spinner-dark"
                style={{ width: 24, height: 24, margin: '20px auto 0', display: 'block' }} />
            </div>

          ) : (
            <>
              {/* Header */}
              <div className="auth-hdr">
                <h2>Verify your email</h2>
                <p>
                  We sent a 6-digit code to{' '}
                  {email
                    ? <strong style={{ color: 'var(--text-dark)', wordBreak: 'break-all' }}>{email}</strong>
                    : 'your email address'
                  }.
                  <br />Enter it below to activate your account.
                </p>
              </div>

              {/* Error banner */}
              {error && (
                <div className="auth-alert auth-alert-error" style={{ marginBottom: 18 }}>
                  <i className="fas fa-circle-exclamation" style={{ marginTop: 1, flexShrink: 0 }} />
                  <span>{error}</span>
                </div>
              )}

              {/* ── OTP BOXES — CSS Grid so each box is 1/6 of the shell width ── */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 8,
                width: '100%',
                marginBottom: 24,
              }}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                    style={{
                      width: '100%',        /* fill the grid column  */
                      aspectRatio: '1 / 1', /* always a perfect square */
                      minWidth: 0,          /* allow shrink below content size */
                      boxSizing: 'border-box',
                      padding: 0,
                      textAlign: 'center',
                      fontSize: 'clamp(16px, 5vw, 24px)',
                      fontWeight: 800,
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      color: 'var(--text-dark)',
                      background: d ? 'rgba(0,44,237,.05)' : 'var(--surface)',
                      border: `2px solid ${
                        error ? 'rgba(255,59,48,.45)'
                        : d   ? 'var(--primary)'
                               : 'var(--border)'
                      }`,
                      borderRadius: 12,
                      outline: 'none',
                      transition: 'border-color .18s, background .18s, box-shadow .18s',
                      boxShadow: d ? '0 0 0 3px rgba(0,44,237,.08)' : 'none',
                      caretColor: 'transparent',
                    }}
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                className="btn-auth"
                disabled={loading || !isComplete}
                onClick={handleSubmit}
                style={{ marginBottom: 12 }}
              >
                {loading
                  ? <><span className="spinner" /> Verifying…</>
                  : <><i className="fas fa-shield-check" /> Verify Email</>
                }
              </button>

              {/* Resend button */}
              <button
                className="btn-auth"
                disabled={resending || cooldown > 0}
                onClick={handleResend}
                style={{
                  marginBottom: 20,
                  background: 'transparent',
                  color: cooldown > 0 ? 'var(--text-light)' : 'var(--primary)',
                  border: '1.5px solid',
                  borderColor: cooldown > 0 ? 'var(--border)' : 'var(--primary)',
                }}
              >
                {resending
                  ? <><span className="spinner" style={{ borderTopColor: 'var(--primary)' }} /> Sending…</>
                  : cooldown > 0
                    ? <><i className="fas fa-clock" /> Resend in {cooldown}s</>
                    : <><i className="fas fa-paper-plane" /> Resend OTP</>
                }
              </button>

              <div className="auth-bottom">
                <Link to="/login"><i className="fas fa-arrow-left" /> Back to Login</Link>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}